// Atoms derivados. Se recalculan automáticamente cuando cambia algún input.
// Esto reemplaza por completo el patrón useEffect/setState para sincronización.

import { atom } from "jotai";
import {
  oracleJsonAtom,
  audioFileAtom,
  audioSrcAtom,
  customSpeakersAtom,
  profilesAtom,
  manualOverridesAtom,
} from "./atoms.ts";
import { segmentFromTokens } from "../lib/segment-from-tokens.ts";
import { findSpeakerRuns, pickTopSamples } from "../lib/samples.ts";
import type { SpeakerRun } from "../lib/samples.ts";
import { generateTaggedSrt } from "../lib/generate.ts";
import { audioFileToUrl, revokeAudioUrl } from "../platform/browser-fs.ts";
import type { TokenSegment } from "../lib/types.ts";

// --- URL del audio (para el <audio>) ---

/**
 * URL del audio actual para el elemento <audio>, o null si no hay audio.
 *
 * Fuentes en orden de prioridad:
 *   1. audioFileAtom — File en memoria (subido en esta sesión).
 *      → Object URL via URL.createObjectURL. Más rápido, no expira.
 *   2. audioSrcAtom — PAR de download del bucket (flujo de continuación).
 *      → URL absoluta a OCI, expira en 10 min. El hook useAudioSrcRefresh
 *        la renueva automáticamente en IdentifyScreen y ReviewScreen.
 *
 * Cache local para el File: para un mismo File, devolvemos siempre la
 * misma object URL en lugar de crear una nueva en cada read (lo cual
 * filtraría memoria). Cuando el File cambia o se limpia, revocamos la
 * URL anterior.
 */
let cachedFile: File | null = null;
let cachedUrl: string | null = null;

export const audioUrlAtom = atom<string | null>((get) => {
  const file = get(audioFileAtom);

  if (file !== null) {
    // File en memoria — tiene prioridad sobre la PAR del bucket.
    if (file === cachedFile) return cachedUrl;
    if (cachedUrl) revokeAudioUrl(cachedUrl);
    cachedFile = file;
    cachedUrl = audioFileToUrl(file);
    return cachedUrl;
  }

  // No hay File. Limpiar el cache si quedó algo de una sesión anterior.
  if (cachedFile !== null) {
    if (cachedUrl) revokeAudioUrl(cachedUrl);
    cachedFile = null;
    cachedUrl = null;
  }

  // Fallback: PAR del bucket (meetings existentes abiertos desde la lista).
  const src = get(audioSrcAtom);
  return src?.url ?? null;
});

// --- Segmentación desde tokens ---

/**
 * Segmentos generados directamente desde los tokens del JSON de OCI.
 * Se recalcula automáticamente cuando cambia oracleJsonAtom.
 *
 * Reemplaza al antiguo matchResultAtom (que cruzaba srtSegmentsAtom con
 * los tokens via overlap de tiempo). Ahora los segmentos vienen directo
 * de los tokens: sin matching heurístico, sin multi-speaker ambiguo.
 */
export const tokenSegmentsAtom = atom<TokenSegment[]>((get) => {
  const oracle = get(oracleJsonAtom);
  if (!oracle) return [];
  return segmentFromTokens(oracle.tokens);
});

// --- Lista de speakers (detectados + custom) ---

/**
 * Índices únicos de speakers detectados por Oracle, ordenados ascendente.
 * Se calcula directo de los tokens, no del speakerCount reportado por Oracle.
 */
export const detectedSpeakersAtom = atom<number[]>((get) => {
  const oracle = get(oracleJsonAtom);
  if (!oracle) return [];
  const set = new Set<number>();
  for (const t of oracle.tokens) set.add(t.speakerIndex);
  return [...set].sort((a, b) => a - b);
});

/**
 * Lista combinada de TODOS los speakers (detectados + custom).
 * Detectados primero (orden ascendente), luego custom (orden de creación).
 */
export const speakerListAtom = atom<number[]>((get) => {
  const detected = get(detectedSpeakersAtom);
  const custom = get(customSpeakersAtom);
  // custom viene en orden de creación: [-1, -2, -3, ...]. Los mostramos en
  // ese orden (el primero agregado primero), no por valor.
  return [...detected, ...custom];
});

// --- Samples por speaker (para el botón de play) ---

/**
 * Mapping speakerIndex → SpeakerRun[] (los 3 mejores samples para identificar).
 * Solo aplica a speakers detectados por Oracle. Los custom no tienen audio.
 */
export const samplesPerSpeakerAtom = atom<Map<number, SpeakerRun[]>>((get) => {
  const oracle = get(oracleJsonAtom);
  if (!oracle) return new Map();

  const runs = findSpeakerRuns(oracle.tokens);
  const detected = get(detectedSpeakersAtom);
  const result = new Map<number, SpeakerRun[]>();

  for (const sp of detected) {
    result.set(sp, pickTopSamples(runs, sp, { count: 3 }));
  }
  return result;
});

// --- Segmentos finales con overrides aplicados ---

/**
 * Los TokenSegments con los overrides manuales del usuario aplicados
 * encima. Un override reemplaza el speakerIndex del segmento.
 * Esto es lo que se usa para generar el SRT y mostrar en ReviewScreen.
 */
export const finalSegmentsAtom = atom<TokenSegment[]>((get) => {
  const segments = get(tokenSegmentsAtom);
  const overrides = get(manualOverridesAtom);
  if (overrides.size === 0) return segments;

  return segments.map((seg) => {
    const forced = overrides.get(seg.index);
    if (forced === undefined) return seg;
    return { ...seg, speakerIndex: forced };
  });
});

// --- SRT etiquetado final (preview en vivo) ---

/**
 * El SRT final como string. Devuelve null si faltan datos o si algún
 * speaker no tiene perfil todavía.
 *
 * Recalcula en vivo cuando el usuario tipea un nombre o cargo — esa es
 * la magia de los atoms derivados.
 */
export const taggedSrtAtom = atom<string | null>((get) => {
  const segments = get(finalSegmentsAtom);
  const profiles = get(profilesAtom);
  if (segments.length === 0) return null;

  // Verifica que TODOS los speakers que aparecen en los segmentos tengan perfil.
  const neededSpeakers = new Set(segments.map((s) => s.speakerIndex));
  for (const sp of neededSpeakers) {
    if (!profiles.has(sp)) return null;
  }

  try {
    return generateTaggedSrt(segments, profiles);
  } catch {
    return null;
  }
});

// --- Estado de "puede avanzar" ---

/**
 * true si todos los speakers detectados Y los custom tienen nombre y cargo.
 * Habilita el botón "Continuar" en la pantalla Identify.
 */
export const canProceedFromIdentifyAtom = atom<boolean>((get) => {
  const speakers = get(speakerListAtom);
  const profiles = get(profilesAtom);
  if (speakers.length === 0) return false;
  return speakers.every((sp) => {
    const p = profiles.get(sp);
    return p !== undefined && p.name.trim() !== "" && p.role.trim() !== "";
  });
});
